import * as React from 'react';
import classNames from 'classnames';
import { ErrorLogComponent } from '@cian/error-log-component';
import { Checkbox, Tooltip } from '@cian/ui-kit';
import { IRoad } from '../../types/geo';
import { isArrayWithItems } from '../../utils';
import { IRoadModel, TRoadsProps } from './container';
import { RoadsMap } from './map';
import { trackRoadHovered, trackRoadSelected, trackSubroadSelected } from './tracking';
import * as styles from './Roads.css';
import { isEmpty } from '../../utils/isEmpty';
import { AppContext } from '../../containers/App/AppContext';
import { TOOLTIP_NO_OFFER_TEXT } from '../../constants';

export interface IRoadsState {
  hoveredRoads: number[];
  roadsQuery: string;
}

interface IDirectionView {
  id: number;
  name: string;
}

export class Roads extends React.Component<TRoadsProps, IRoadsState> {
  public state = {
    hoveredRoads: [],
    roadsQuery: '',
  };

  public componentDidMount() {
    this.props.onChangeSubroads(this.props.roads.selectedRoads, true);
  }

  public render() {
    const { hoveredRoads } = this.state;
    const { groupedRoads, regionId, roads } = this.props;
    const { selectedRoads } = roads;
    if (isEmpty(groupedRoads)) {
      return null;
    }

    return (
      <div className={styles['container']}>
        <div className={styles['list']}>{this.preparedRoads.map(this.renderRoad)}</div>
        <AppContext.Consumer>
          {context => (
            <ErrorLogComponent logger={context.logger}>
              <RoadsMap
                hovered={hoveredRoads}
                regionId={regionId}
                selectedRoads={selectedRoads}
                httpApi={context.httpApi}
                telemetry={context.telemetry}
                config={context.config}
              />
            </ErrorLogComponent>
          )}
        </AppContext.Consumer>
      </div>
    );
  }

  private get preparedRoads(): IDirectionView[] {
    return Object.keys(this.props.groupedRoads)
      .reduce((acc: IDirectionView[], value: string) => {
        const id = +value;

        return Number.isFinite(id) ? [...acc, { id, name: this.getDirectionName(id) }] : acc;
      }, [])
      .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : -1));
  }

  private getDirectionName = (id: number) => {
    const {
      groupedRoads,
      roads: { currentRoads },
    } = this.props;
    const found = currentRoads.find(({ directionId }) => directionId === id);
    if (found) {
      return found.directionName;
    } else {
      return isArrayWithItems(groupedRoads[id]) ? groupedRoads[id][0].name : '';
    }
  };

  private renderRoad = ({ id, name }: IDirectionView) => {
    const subroads = this.props.groupedRoads[id];
    if (!isArrayWithItems(subroads)) {
      return null;
    }
    const isRoadSingle = Boolean((subroads[0] as IRoadModel).withoutSubroads);
    const availableRoads = this.getAvailableRoads(subroads);
    const ids = subroads.map(subroad => subroad.id);
    const disabled = isRoadSingle
      ? !(this.isSubroadEnabled(ids[0]) || this.filterSelectedRoads(subroads[0]))
      : !availableRoads.length;
    const checked = isRoadSingle
      ? this.filterSelectedRoads(subroads[0])
      : !disabled && availableRoads.every(this.filterSelectedRoads);

    const checkboxElement = (
      <Checkbox
        label={name}
        disabled={disabled}
        value={String(id)}
        checked={checked}
        onChange={(_e, value) => this.changeRoad(value, subroads, isRoadSingle, name)}
      />
    );

    return (
      <div key={id}>
        <div
          className={classNames(styles['list-item'], styles['list-item--road'])}
          onMouseOver={this.onMouseOverRoad(ids, name, false)}
          onMouseOut={this.onMouseOutRoad}
        >
          {disabled ? (
            <Tooltip title={TOOLTIP_NO_OFFER_TEXT} placement="right">
              {checkboxElement}
            </Tooltip>
          ) : (
            checkboxElement
          )}
        </div>
        {!isRoadSingle && this.renderSubroads(subroads)}
      </div>
    );
  };

  private renderSubroads = (roads: IRoad[]) => (
    <div className={styles['list-subroads']}>
      {roads.map(({ id, name }) => {
        const disabled = !this.isSubroadEnabled(id);
        const checked = this.props.roads.selectedRoads.includes(id);
        const checkboxElement = (
          <Checkbox
            label={name}
            value={String(id)}
            checked={checked}
            onChange={(_e, checked) => this.onSubroadValueChange(id, checked, name)}
            disabled={!checked && disabled}
          />
        );

        return (
          <div
            key={id}
            className={styles['list-item']}
            onMouseOver={this.onMouseOverRoad([id], name, true)}
            onMouseOut={this.onMouseOutRoad}
          >
            {disabled ? (
              <Tooltip title={TOOLTIP_NO_OFFER_TEXT} placement="right">
                <span className={styles['checkbox--disabled']}>{checkboxElement}</span>
              </Tooltip>
            ) : (
              checkboxElement
            )}
          </div>
        );
      })}
    </div>
  );

  private onSubroadValueChange = (id: number, checked: boolean, label: string) => {
    if (checked) {
      trackSubroadSelected(label);
    }
    this.props.onChangeSubroads([id], checked);
  };

  private isSubroadEnabled = (id: number) => {
    const subroadDistance = (this.props.roadData as Record<number, number>)[id];

    return !!subroadDistance && subroadDistance <= this.props.roads.roadDistance;
  };

  private changeRoad = (checked: boolean, roads: IRoad[], isRoadSingle: boolean, label: string) => {
    if (checked && label) {
      trackRoadSelected(label);
    }
    const selectedRoads = !checked || isRoadSingle ? roads : this.getAvailableRoads(roads);
    const ids = selectedRoads.map(({ id }) => id);
    this.props.onChangeSubroads(ids, checked);
  };

  private getAvailableRoads = (roads: IRoad[]) =>
    roads.reduce((acc, road) => (this.isSubroadEnabled(road.id) ? acc.concat(road) : acc), [] as IRoad[]);

  private filterSelectedRoads = (value: { id: number }) => this.props.roads.selectedRoads.includes(value.id);

  private onMouseOutRoad = () => {
    if (this.state.hoveredRoads.length) {
      this.setState({
        hoveredRoads: [],
      });
    }
  };

  private onMouseOverRoad = (hoveredRoads: number[], name: string, isPart: boolean) => {
    return () => {
      trackRoadHovered(name, isPart);
      this.setState({
        hoveredRoads,
      });
    };
  };
}
